import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  MessageSquare, TrendingUp, Send, Sparkles, ChevronDown, ChevronUp, Star,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REACTION_EMOJIS = ['👍', '💡', '🤔', '🙋'];

export default function CoPSignalCard({ signal, currentUserId, isStaff, headers, typeLabels, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [busy, setBusy] = useState(false);

  const typeMeta = typeLabels[signal.signal_type] || typeLabels.observation;
  const TypeIcon = typeMeta.icon;

  const reactionVoters = signal.reaction_voters || {};
  const commentCount = (signal.comments || []).length;
  const relevanceVotes = signal.relevance_votes || {};
  const avgRelevance = Object.values(relevanceVotes).length
    ? (Object.values(relevanceVotes).reduce((a, b) => a + b, 0) / Object.values(relevanceVotes).length).toFixed(1)
    : null;
  const myVote = relevanceVotes[currentUserId];

  const isResonant = signal.status === 'resonant' || signal.status === 'synthesised';

  const toggleReaction = async (emoji) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/cop/signals/${signal.signal_id}/react`, { emoji }, { headers });
      onChanged(res.data.signal);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to react');
    } finally { setBusy(false); }
  };

  const castVote = async (vote) => {
    setBusy(true);
    try {
      await axios.post(`${API}/cop/signals/${signal.signal_id}/vote`, { vote }, { headers });
      // Refetch the signal to get updated state
      const res = await axios.get(`${API}/cop/signals/${signal.signal_id}`, { headers });
      onChanged(res.data);
      toast.success(`Relevance: ${vote}/5`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to vote');
    } finally { setBusy(false); }
  };

  const submitComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    setBusy(true);
    try {
      await axios.post(`${API}/cop/signals/${signal.signal_id}/comment`, { text }, { headers });
      const res = await axios.get(`${API}/cop/signals/${signal.signal_id}`, { headers });
      onChanged(res.data);
      setCommentText('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Comment failed');
    } finally { setBusy(false); }
  };

  const upgradeToResonant = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/cop/signals/${signal.signal_id}/upgrade`, {}, { headers });
      const res = await axios.get(`${API}/cop/signals/${signal.signal_id}`, { headers });
      onChanged(res.data);
      toast.success('Upgraded to resonant — ready for synthesis (Stage 3)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upgrade failed');
    } finally { setBusy(false); }
  };

  const synthesizeNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/cop/signals/${signal.signal_id}/synthesize`, {}, { headers });
      const msg = r.data.status === 'exists'
        ? 'A draft already exists — review it in the Synthesis drafts panel'
        : `Draft ready: ${r.data.draft.object_type.replace('_', ' ')}`;
      toast.success(msg);
      // Trigger a parent refresh so the SynthesisDraftsCard picks it up
      window.dispatchEvent(new CustomEvent('cop:synthesis-drafted'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Synthesis failed');
    } finally { setBusy(false); }
  };

  return (
    <Card
      className={`border transition-all ${isResonant ? 'border-[#FF9500]/40 bg-[#FF9500]/3' : 'border-[#E5E7EB]'}`}
      data-testid={`signal-${signal.signal_id}`}
    >
      <CardContent className="p-4">
        {/* Header row */}
        <div className="flex items-start gap-3 mb-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${typeMeta.color}15` }}
          >
            <TypeIcon className="w-4 h-4" style={{ color: typeMeta.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#0A0A0A] leading-relaxed" data-testid={`signal-description-${signal.signal_id}`}>
              {signal.description}
            </p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-[10px] text-[#9CA3AF]">
                {signal.creator_name} · {format(new Date(signal.created_at), 'MMM d, HH:mm')}
              </span>
              <Badge
                className="text-[9px] border-0"
                style={{ backgroundColor: `${typeMeta.color}15`, color: typeMeta.color }}
              >
                {typeMeta.label}
              </Badge>
              {isResonant && (
                <Badge className="text-[9px] bg-[#FF9500]/15 text-[#FF9500] border-0" data-testid={`resonant-badge-${signal.signal_id}`}>
                  <Sparkles className="w-2.5 h-2.5 mr-0.5" />Resonant
                </Badge>
              )}
              {(signal.ontology_classes || []).map(c => (
                <Badge key={c} variant="outline" className="text-[9px] text-[#4B5563] border-[#E5E7EB]">
                  {c}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Reactions row */}
        <div className="flex items-center gap-2 flex-wrap mt-3" data-testid={`reactions-${signal.signal_id}`}>
          {REACTION_EMOJIS.map(emoji => {
            const voters = reactionVoters[emoji] || [];
            const mine = voters.includes(currentUserId);
            return (
              <button
                key={emoji}
                onClick={() => toggleReaction(emoji)}
                disabled={busy}
                data-testid={`react-${emoji}-${signal.signal_id}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                  mine
                    ? 'bg-[#002FA7]/10 border-[#002FA7]/30 text-[#002FA7]'
                    : 'bg-white border-[#E5E7EB] text-[#4B5563] hover:bg-[#F9FAFB]'
                }`}
              >
                <span>{emoji}</span>
                {voters.length > 0 && <span className="font-medium">{voters.length}</span>}
              </button>
            );
          })}

          {/* Relevance stars */}
          <div className="inline-flex items-center gap-0.5 ml-2 border-l border-[#E5E7EB] pl-2" data-testid={`vote-${signal.signal_id}`}>
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                onClick={() => castVote(n)}
                disabled={busy}
                data-testid={`vote-${n}-${signal.signal_id}`}
                title={`Rate ${n}/5`}
                className="p-0.5 transition-colors"
              >
                <Star
                  className="w-3.5 h-3.5"
                  style={{
                    color: myVote && myVote >= n ? '#FF9500' : '#D1D5DB',
                    fill: myVote && myVote >= n ? '#FF9500' : 'none',
                  }}
                />
              </button>
            ))}
            {avgRelevance && (
              <span className="text-[10px] text-[#4B5563] ml-1" data-testid={`avg-relevance-${signal.signal_id}`}>
                {avgRelevance}
              </span>
            )}
          </div>

          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-auto text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-1"
            data-testid={`toggle-${signal.signal_id}`}
          >
            <MessageSquare className="w-3 h-3" />{commentCount} {commentCount === 1 ? 'comment' : 'comments'}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {/* Expanded section: similar signals · experts · comments · upgrade */}
        {expanded && (
          <div className="mt-4 pt-3 border-t border-[#F3F4F6] space-y-3" data-testid={`expanded-${signal.signal_id}`}>
            {signal.similar_signals?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1">
                  Similar signals
                </p>
                <div className="space-y-1">
                  {signal.similar_signals.slice(0, 3).map(s => (
                    <div key={s.signal_id} className="text-[11px] text-[#4B5563] p-2 rounded bg-[#F9FAFB]" data-testid={`similar-${s.signal_id}`}>
                      "{s.description.slice(0, 120)}{s.description.length > 120 ? '…' : ''}"
                      <span className="text-[#9CA3AF] ml-1">· {s.creator_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {signal.expert_suggestions?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1">
                  Suggested SMEs (load-balanced)
                </p>
                <div className="flex gap-1.5 flex-wrap">
                  {signal.expert_suggestions.map(e => (
                    <Badge key={e.user_id} className="text-[10px] bg-[#5856D6]/10 text-[#5856D6] border-0" data-testid={`expert-${e.user_id}`}>
                      {e.name} · {e.role}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Comments list */}
            <div className="space-y-2">
              {(signal.comments || []).map(c => (
                <div key={c.comment_id} className="text-[11px]" data-testid={`comment-${c.comment_id}`}>
                  <span className="font-semibold text-[#0A0A0A]">{c.user_name}</span>
                  <span className="text-[#9CA3AF] ml-2">{format(new Date(c.created_at), 'MMM d, HH:mm')}</span>
                  <p className="text-[#4B5563] mt-0.5">{c.text}</p>
                </div>
              ))}
            </div>

            {/* Comment composer */}
            <div className="flex gap-1.5">
              <Input
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Add a short perspective…"
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                className="text-xs h-8"
                maxLength={500}
                data-testid={`comment-input-${signal.signal_id}`}
              />
              <Button
                size="sm"
                onClick={submitComment}
                disabled={busy || !commentText.trim()}
                className="bg-[#002FA7] hover:bg-[#00227A] h-8"
                data-testid={`comment-submit-${signal.signal_id}`}
              >
                <Send className="w-3 h-3" />
              </Button>
            </div>

            {/* Steward actions */}
            {isStaff && !isResonant && (
              <div className="pt-2 border-t border-[#F3F4F6]">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={upgradeToResonant}
                  disabled={busy}
                  className="h-7 text-[11px] border-[#FF9500]/40 text-[#FF9500] hover:bg-[#FF9500]/5"
                  data-testid={`upgrade-${signal.signal_id}`}
                >
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Upgrade to resonant (steward action)
                </Button>
              </div>
            )}
            {isStaff && isResonant && signal.status === 'resonant' && !signal.synthesis_draft_id && (
              <div className="pt-2 border-t border-[#F3F4F6]">
                <Button
                  size="sm"
                  onClick={synthesizeNow}
                  disabled={busy}
                  className="h-7 text-[11px] bg-[#5856D6] hover:bg-[#443EAA] text-white"
                  data-testid={`synthesize-${signal.signal_id}`}
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  Synthesize now (Stage 3)
                </Button>
              </div>
            )}
            {signal.status === 'synthesised' && (
              <div className="pt-2 border-t border-[#F3F4F6]">
                <Badge className="text-[10px] bg-[#34C759]/15 text-[#34C759] border-0" data-testid={`synthesised-badge-${signal.signal_id}`}>
                  ✓ Synthesised into a knowledge asset
                </Badge>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
